import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import {
  ContentActions,
  APPROVE_ACTION_COLORS,
  REJECT_ACTION_COLORS,
  EDIT_ACTION_COLORS,
} from "@/components/ui/ContentActions";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import type { BrandingLocalization } from "@/lib/api/review";
import {
  ReviewField,
  FieldRow,
  Divider,
  FieldDecisionActions,
  type FieldDecision,
  type RejectFieldMeta,
  type FieldRejectionData,
  type EditFieldMeta,
  type FieldEditData,
} from "./Shared";
import { FilePreview } from "@/components/ui/FilePreview";

interface BrandingLocalizationSectionProps {
  branding: BrandingLocalization | undefined;
  fieldDecisions: Record<string, FieldDecision>;
  fieldRejectionData: Record<string, FieldRejectionData>;
  fieldEditData: Record<string, FieldEditData>;
  onDecide: (fieldKey: string, decision: FieldDecision) => void;
  onRejectField: (fieldKey: string, meta: RejectFieldMeta) => void;
  onEditField: (fieldKey: string, meta: EditFieldMeta) => void;
  onUndoDecision: (fieldKey: string) => void;
  onShowReason: (fieldKey: string) => void;
  onShowEdit: (fieldKey: string) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function BrandingLocalizationSection({
  branding,
  fieldDecisions,
  fieldRejectionData: _fieldRejectionData,
  fieldEditData: _fieldEditData,
  onDecide,
  onRejectField,
  onEditField,
  onUndoDecision,
  onShowReason,
  onShowEdit,
  applicationId,
  t,
}: BrandingLocalizationSectionProps) {
  const viewOnly = t("review.review.viewOnly");
  const notProvided = t("review.application.brandingLocalization.notProvided");

  return (
    <View className="items-start w-full">
      {/* Card header */}
      <View className="px-6 py-5 border-b border-stroke-50 items-start w-full">
        <AppText weight="semibold" className="text-lg text-dark-text-50">
          {t("review.application.brandingLocalization.title")}
        </AppText>
      </View>

      {/* Card body */}
      <View className="px-8 py-6 w-full">
        <View className="gap-4 items-start w-full">
          {/* Short Name English + Arabic — side by side */}
          <FieldRow>
            {/* English — Approve/Reject/Edit */}
            <View className="flex-1 gap-3 items-start w-full">
              <AppText weight="semibold" className="text-base text-supporting-text-40">
                {t("review.application.brandingLocalization.fields.shortNameEnglish")}
              </AppText>
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {branding?.shortNameEnglish ?? notProvided}
              </AppText>
              {fieldDecisions["shortNameEnglish"] ? (
                <FieldDecisionActions
                  fieldKey="shortNameEnglish"
                  decision={fieldDecisions["shortNameEnglish"]}
                  onUndoDecision={onUndoDecision}
                  onShowReason={onShowReason}
                  onShowEdit={onShowEdit}
                  t={t}
                />
              ) : (
                <ContentActions
                  actions={[
                    {
                      label: "review.application.approve",
                      onPress: () => onDecide("shortNameEnglish", "approve"),
                      icon: (
                        <Icon
                          as={Feather}
                          name="check-circle"
                          size={14}
                          className="text-green-50"
                        />
                      ),
                      ...APPROVE_ACTION_COLORS,
                    },
                    {
                      label: "review.application.reject",
                      onPress: () =>
                        onRejectField("shortNameEnglish", {
                          label: t(
                            "review.application.brandingLocalization.fields.shortNameEnglish"
                          ),
                          value: branding?.shortNameEnglish,
                        }),
                      icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                      ...REJECT_ACTION_COLORS,
                    },
                    {
                      label: "review.application.edit",
                      onPress: () =>
                        onEditField("shortNameEnglish", {
                          label: t(
                            "review.application.brandingLocalization.fields.shortNameEnglish"
                          ),
                          currentValue: branding?.shortNameEnglish,
                        }),
                      icon: <Icon as={Feather} name="edit-2" size={14} className="text-blue-50" />,
                      ...EDIT_ACTION_COLORS,
                    },
                  ]}
                />
              )}
            </View>
          </FieldRow>

          {/* Arabic - VIEW ONLY, Edit only */}
          <FieldRow>
            <ReviewField
              label={t("review.application.brandingLocalization.fields.shortNameArabic")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {branding?.shortNameArabic ?? notProvided}
              </AppText>
              {fieldDecisions["shortNameArabic"] ? (
                <FieldDecisionActions
                  fieldKey="shortNameArabic"
                  decision={fieldDecisions["shortNameArabic"]}
                  onUndoDecision={onUndoDecision}
                  onShowReason={onShowReason}
                  onShowEdit={onShowEdit}
                  t={t}
                />
              ) : (
                <ContentActions
                  actions={[
                    {
                      label: "review.application.edit",
                      onPress: () =>
                        onEditField("shortNameArabic", {
                          label: t(
                            "review.application.brandingLocalization.fields.shortNameArabic"
                          ),
                          currentValue: branding?.shortNameArabic,
                        }),
                      icon: <Icon as={Feather} name="edit-2" size={14} className="text-blue-50" />,
                      ...EDIT_ACTION_COLORS,
                    },
                  ]}
                />
              )}
            </ReviewField>
          </FieldRow>
        </View>

        <Divider />

        {/* Business Description (English) — full width, Approve/Reject/Edit */}
        <View className="gap-3 items-start w-full">
          <AppText weight="semibold" className="text-base text-supporting-text-40">
            {t("review.application.brandingLocalization.fields.descriptionEnglish")}
          </AppText>
          <AppText weight="medium" className="text-lg text-dark-text-50 leading-6">
            {branding?.descriptionEnglish ?? notProvided}
          </AppText>
          {fieldDecisions["descriptionEnglish"] ? (
            <FieldDecisionActions
              fieldKey="descriptionEnglish"
              decision={fieldDecisions["descriptionEnglish"]}
              onUndoDecision={onUndoDecision}
              onShowReason={onShowReason}
              onShowEdit={onShowEdit}
              t={t}
            />
          ) : (
            <ContentActions
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => onDecide("descriptionEnglish", "approve"),
                  icon: (
                    <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                  ),
                  ...APPROVE_ACTION_COLORS,
                },
                {
                  label: "review.application.reject",
                  onPress: () =>
                    onRejectField("descriptionEnglish", {
                      label: t("review.application.brandingLocalization.fields.descriptionEnglish"),
                      value: branding?.descriptionEnglish,
                    }),
                  icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                  ...REJECT_ACTION_COLORS,
                },
                {
                  label: "review.application.edit",
                  onPress: () =>
                    onEditField("descriptionEnglish", {
                      label: t("review.application.brandingLocalization.fields.descriptionEnglish"),
                      currentValue: branding?.descriptionEnglish,
                      multiline: true,
                    }),
                  icon: <Icon as={Feather} name="edit-2" size={14} className="text-blue-50" />,
                  ...EDIT_ACTION_COLORS,
                },
              ]}
            />
          )}
        </View>

        <Divider />

        {/* Business Description (Arabic) — full width, VIEW ONLY, Edit only */}
        <ReviewField
          label={t("review.application.brandingLocalization.fields.descriptionArabic")}
          viewOnlyLabel={viewOnly}
        >
          <AppText weight="medium" className="text-lg text-dark-text-50 leading-6">
            {branding?.descriptionArabic ?? notProvided}
          </AppText>
          {fieldDecisions["descriptionArabic"] ? (
            <FieldDecisionActions
              fieldKey="descriptionArabic"
              decision={fieldDecisions["descriptionArabic"]}
              onUndoDecision={onUndoDecision}
              onShowReason={onShowReason}
              onShowEdit={onShowEdit}
              t={t}
            />
          ) : (
            <ContentActions
              actions={[
                {
                  label: "review.application.edit",
                  onPress: () =>
                    onEditField("descriptionArabic", {
                      label: t("review.application.brandingLocalization.fields.descriptionArabic"),
                      currentValue: branding?.descriptionArabic,
                      multiline: true,
                    }),
                  icon: <Icon as={Feather} name="edit-2" size={14} className="text-blue-50" />,
                  ...EDIT_ACTION_COLORS,
                },
              ]}
            />
          )}
        </ReviewField>

        <Divider />

        {/* Business Logo — full width, Approve/Reject */}
        <View className="gap-3 items-start w-full">
          <AppText weight="semibold" className="text-base text-supporting-text-40">
            {t("review.application.brandingLocalization.fields.businessLogo")}
          </AppText>
          {branding?.logoUrl ? (
            <FilePreview
              className="max-w-[320px]"
              fileKey={branding?.logoUrl}
              isVendor={false}
              applicationId={applicationId}
            />
          ) : (
            <View className="max-w-[229px] max-h-[160px] bg-white rounded-xl border border-stroke-30 items-center justify-center">
              <AppText className="text-sm text-dark-text-20 text-center">
                {t("review.application.brandingLocalization.noLogo")}
              </AppText>
            </View>
          )}
          {fieldDecisions["businessLogo"] ? (
            <FieldDecisionActions
              fieldKey="businessLogo"
              decision={fieldDecisions["businessLogo"]}
              onUndoDecision={onUndoDecision}
              onShowReason={onShowReason}
              onShowEdit={onShowEdit}
              t={t}
            />
          ) : (
            <ContentActions
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => onDecide("businessLogo", "approve"),
                  icon: (
                    <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                  ),
                  ...APPROVE_ACTION_COLORS,
                },
                {
                  label: "review.application.reject",
                  onPress: () =>
                    onRejectField("businessLogo", {
                      label: t("review.application.brandingLocalization.fields.businessLogo"),
                      fileKey: branding?.logoUrl,
                    }),
                  icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                  ...REJECT_ACTION_COLORS,
                },
              ]}
            />
          )}
        </View>
      </View>
    </View>
  );
}
